import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORIES = ['DOB', 'FDNY', 'DOT', 'General'];
const CAT_COLORS = {
  DOB: { bg: '#EFF6FF', fg: '#1B4A76' },
  FDNY: { bg: '#FEF2F2', fg: '#E5441A' },
  DOT: { bg: '#FFFBEB', fg: '#D97706' },
  General: { bg: '#F4F4F5', fg: '#031333' },
};
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const EMPTY = { name: '', description: '', default_fee: 0, category: 'DOB', number: '' };

function CategoryPill({ category }) {
  const c = CAT_COLORS[category] || CAT_COLORS.General;
  return <span style={{ backgroundColor: c.bg, color: c.fg, borderRadius: '2px', fontSize: '12px', padding: '2px 8px', fontFamily: "'DM Sans', sans-serif" }}>{category}</span>;
}

export default function TaskLibraryPage() {
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const isAdmin = effectiveRole === 'Administrator' || effectiveRole === 'Owner';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchItems = () =>
    api.get('/proposal-task-library?include_inactive=true')
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));

  useEffect(() => { fetchItems().finally(() => setLoading(false)); }, []);

  const openAdd = () => {
    const nextNum = items.length ? Math.max(...items.map(i => i.number || 0)) + 1 : 1;
    setForm({ ...EMPTY, number: nextNum });
    setAddOpen(true);
  };

  const createItem = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post('/proposal-task-library', { ...form, default_fee: Number(form.default_fee) || 0, number: Number(form.number) || undefined });
      toast.success('Service added');
      setAddOpen(false);
      fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding service'); }
    setSaving(false);
  };

  const saveField = async (id, patch) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));
    try { await api.put(`/proposal-task-library/${id}`, patch); }
    catch { toast.error('Could not save'); fetchItems(); }
  };

  const toggleActive = async (item) => {
    if (item.is_active) {
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, is_active: false } : i)));
      try { await api.delete(`/proposal-task-library/${item.id}`); }
      catch { toast.error('Could not deactivate'); fetchItems(); }
    } else {
      saveField(item.id, { is_active: true });
    }
  };

  if (!isAdmin) {
    return (
      <div className="py-12 text-center" data-testid="task-library-forbidden">
        <p className="text-sm text-muted-foreground">This page is available to Administrators only.</p>
        <Button variant="outline" className="mt-3 border-border" onClick={() => navigate('/')}>Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="task-library-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-semibold text-lg tracking-tight text-foreground">Standard Task Library</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Reusable service line items used when building proposals.</p>
        </div>
        <Button className="bg-primary text-primary-foreground" onClick={openAdd} data-testid="add-service-btn"><Plus className="h-4 w-4 mr-1" /> Add Service</Button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="w-16 text-xs text-muted-foreground">#</TableHead>
              <TableHead className="text-xs text-muted-foreground">Name</TableHead>
              <TableHead className="w-28 text-xs text-muted-foreground">Category</TableHead>
              <TableHead className="w-32 text-xs text-muted-foreground text-right">Default Fee</TableHead>
              <TableHead className="w-24 text-xs text-muted-foreground text-center">Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">Loading...</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No services yet.</TableCell></TableRow>
            ) : items.map(it => (
              <TableRow key={it.id} className="border-border hover:bg-secondary/20" style={{ opacity: it.is_active ? 1 : 0.5 }} data-testid={`library-row-${it.id}`}>
                <TableCell style={{ fontFamily: "'DM Mono', monospace" }}>{it.number}</TableCell>
                <TableCell>
                  <Input value={it.name || ''} onChange={e => setItems(prev => prev.map(x => x.id === it.id ? { ...x, name: e.target.value } : x))} onBlur={e => saveField(it.id, { name: e.target.value })} className="bg-background border-border h-8 text-sm" data-testid={`library-name-${it.id}`} />
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{it.description}</p>
                </TableCell>
                <TableCell>
                  <Select value={it.category} onValueChange={v => saveField(it.id, { category: v })}>
                    <SelectTrigger className="bg-background border-border h-8 text-xs w-24" data-testid={`library-category-${it.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">{CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-sm">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Input type="number" value={it.default_fee ?? 0} onChange={e => setItems(prev => prev.map(x => x.id === it.id ? { ...x, default_fee: e.target.value } : x))} onBlur={e => saveField(it.id, { default_fee: Number(e.target.value) || 0 })} className="bg-background border-border h-8 text-sm text-right" style={{ fontFamily: "'DM Mono', monospace" }} data-testid={`library-fee-${it.id}`} />
                </TableCell>
                <TableCell className="text-center">
                  <Switch checked={!!it.is_active} onCheckedChange={() => toggleActive(it)} data-testid={`library-active-${it.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="bg-card border-border w-full sm:max-w-md overflow-y-auto" data-testid="add-service-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: '#010A21' }}>Add Service</SheetTitle>
            <SheetDescription>Add a reusable service to the standard task library.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-5">
            <div><Label className="text-xs text-muted-foreground">Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border mt-1" data-testid="service-name-input" /></div>
            <div><Label className="text-xs text-muted-foreground">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="bg-background border-border mt-1" data-testid="service-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Default Fee</Label><Input type="number" value={form.default_fee} onChange={e => setForm({ ...form, default_fee: e.target.value })} className="bg-background border-border mt-1" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="service-fee-input" /></div>
              <div><Label className="text-xs text-muted-foreground">Number</Label><Input type="number" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} className="bg-background border-border mt-1" data-testid="service-number-input" /></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-background border-border mt-1" data-testid="service-category-input"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-sm">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90" disabled={saving} onClick={createItem} data-testid="service-submit-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</> : 'Add Service'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
